import type { ReactNode, TouchEvent } from "react";
import { wasTouchTap } from "../../lib/touch.ts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../molecules/shadcn/dialog.tsx";

// The in-game app's ConfirmationModal (platform-2.0 core/components/ui/
// confirmation-modal): gray cancel, blue confirm, normal case — not the
// practice Button, which is uppercase.
const BTN =
  "inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50";

/**
 * iOS Safari can swallow the first tap on a dialog button over a heavy page
 * (Special Teams had this on its own Check For Updates buttons): run it on
 * touchend and drop the synthetic click, so it runs once.
 */
const onTap = (action: () => void) => (event: TouchEvent<HTMLButtonElement>) => {
  if (!wasTouchTap() || event.currentTarget.disabled) return;
  event.preventDefault();
  action();
};

export function ConfirmationModal({
  visible,
  title,
  children,
  onClose,
  onConfirm,
  cancelText = "Cancel",
  confirmText = "Confirm",
  isLoading = false,
  loadingText = "Loading...",
}: {
  visible: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  cancelText?: string;
  confirmText?: string;
  isLoading?: boolean;
  /** Shown on the confirm button while `isLoading`. */
  loadingText?: string;
}) {
  return (
    <Dialog open={visible} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="max-w-sm rounded border border-white/20 bg-white sm:max-w-sm"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-left text-sm text-gray-600">{children}</div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <button
            type="button"
            className={`${BTN} bg-gray-100 text-black hover:bg-gray-200`}
            onClick={onClose}
            onTouchEnd={onTap(onClose)}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`${BTN} bg-blue-600 text-white hover:bg-blue-700`}
            onClick={onConfirm}
            onTouchEnd={onTap(onConfirm)}
            disabled={isLoading}
          >
            {isLoading ? loadingText : confirmText}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
